/**
 * The HTTP client for the bidtohire.me API.
 *
 * In production the application and the API share an origin: CloudFront routes
 * `/api/*` to the HTTP API and removes the prefix at the edge, so no request
 * the browser makes is ever cross-origin. `VITE_API_URL` overrides the base for
 * local development against a deployed environment.
 */

import type { ApiError } from '@bidtohire/shared';

export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';

/** An error the API reported, carrying the machine-readable code it sent. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Readonly<Record<string, unknown>> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | undefined>>;
  /** Bearer token for the company endpoints. */
  readonly token?: string | undefined;
  readonly signal?: AbortSignal;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path}`;
  if (query === undefined) return url;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === '' ? url : `${url}?${serialized}`;
}

function readErrorBody(status: number, payload: unknown): ApiRequestError {
  const candidate = payload as Partial<ApiError> | null;
  const error = candidate?.error;
  if (error !== undefined && typeof error.message === 'string') {
    return new ApiRequestError(status, error.code ?? 'ERROR', error.message, error.details);
  }
  return new ApiRequestError(
    status,
    'UNEXPECTED',
    'That request failed.',
  );
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.token !== undefined) headers['authorization'] = `Bearer ${options.token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch {
    throw new ApiRequestError(0, 'NETWORK', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text === '' ? null : safeParse(text);

  if (!response.ok) throw readErrorBody(response.status, payload);
  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}
